import { apiClient } from "@/services/apiClient";

export type RequestFeedbackRequestType = "notarial" | "accountant";
export type RequestFeedbackStatus =
  | "pending"
  /** Every reminder was sent (or the link expired) and nobody answered. */
  | "exhausted"
  | "submitted"
  | "reviewed"
  | "archived";

/** Filter status accepted by admin endpoints (synthetic "sent"/"queued" derived from emailSentAt). */
export type AdminStatusFilter =
  | "queued"
  | "sent"
  | "exhausted"
  | "submitted"
  | "reviewed"
  | "archived";

/** Display status derived from (status, emailSentAt) for table rendering. */
export type DisplayStatus =
  | "queued"
  | "sent"
  | "exhausted"
  | "submitted"
  | "reviewed"
  | "archived";

export function deriveDisplayStatus(row: {
  status: RequestFeedbackStatus;
  emailSentAt: string | null;
}): DisplayStatus {
  if (row.status === "pending") {
    return row.emailSentAt ? "sent" : "queued";
  }
  return row.status;
}

export interface PublicSurveyContext {
  productLabel: string | null;
  professionalName: string | null;
  hasProfessional: boolean;
  alreadySubmitted: boolean;
}

export interface SubmitRequestFeedbackPayload {
  platformRating: number;
  professionalRating?: number;
  npsScore: number;
  comment?: string;
}

export interface FeedbackStats {
  total: number;
  promoters: number;
  passives: number;
  detractors: number;
  npsScore: number | null;
  platformAverage: number | null;
  professionalAverage: number | null;
}

export function getAdminFeedbackStats(params: {
  status?: AdminStatusFilter;
  requestType?: RequestFeedbackRequestType;
}): Promise<FeedbackStats> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.requestType) search.set("requestType", params.requestType);
  const qs = search.toString();
  return apiClient
    .get<FeedbackStats>(
      `/platform/request-feedback/stats${qs ? `?${qs}` : ""}`
    )
    .then((res) => res.data);
}

export function getAdminFeedbackDetail(
  id: string
): Promise<AdminRequestFeedbackItem> {
  return apiClient
    .get<AdminRequestFeedbackItem>(
      `/platform/request-feedback/${encodeURIComponent(id)}`
    )
    .then((res) => res.data);
}

export function getPublicSurveyContext(
  token: string
): Promise<PublicSurveyContext> {
  return apiClient
    .get<PublicSurveyContext>(
      `/public/request-feedback/${encodeURIComponent(token)}`
    )
    .then((res) => res.data);
}

export function submitPublicSurvey(
  token: string,
  payload: SubmitRequestFeedbackPayload
): Promise<void> {
  return apiClient
    .post(`/public/request-feedback/${encodeURIComponent(token)}`, payload)
    .then(() => undefined);
}

export interface AdminRequestFeedbackItem {
  id: string;
  requestType: RequestFeedbackRequestType;
  requestId: string;
  productLabel: string | null;
  professionalUserId: string | null;
  professionalName: string | null;
  consumerUserId: string;
  consumerName: string | null;
  consumerEmail: string | null;
  platformRating: number | null;
  professionalRating: number | null;
  npsScore: number | null;
  comment: string | null;
  status: RequestFeedbackStatus;
  scheduledFor: string;
  emailSentAt: string | null;
  submittedAt: string | null;
  createdAt: string;
}

export interface ListAdminRequestFeedbackResponse {
  data: AdminRequestFeedbackItem[];
  total: number;
  page: number;
  limit: number;
}

export function listAdminRequestFeedback(params: {
  page: number;
  limit: number;
  status?: AdminStatusFilter;
  requestType?: RequestFeedbackRequestType;
}): Promise<ListAdminRequestFeedbackResponse> {
  const search = new URLSearchParams();
  search.set("page", String(params.page));
  search.set("limit", String(params.limit));
  if (params.status) search.set("status", params.status);
  if (params.requestType) search.set("requestType", params.requestType);
  return apiClient
    .get<ListAdminRequestFeedbackResponse>(
      `/platform/request-feedback?${search.toString()}`
    )
    .then((res) => res.data);
}

export function updateAdminRequestFeedbackStatus(
  id: string,
  status: RequestFeedbackStatus
): Promise<void> {
  return apiClient
    .patch(`/platform/request-feedback/${encodeURIComponent(id)}`, { status })
    .then(() => undefined);
}

export interface EligibleRequestItem {
  requestType: RequestFeedbackRequestType;
  requestId: string;
  productLabel: string;
  consumerName: string | null;
  consumerEmail: string | null;
  professionalName: string | null;
  createdAt: string;
  /**
   * none       = no survey row yet
   * scheduled  = row exists, nothing sent, first send still due
   * in_cadence = at least one email out, more reminders scheduled
   * exhausted  = every reminder sent (or link expired), nobody answered
   */
  feedbackState: "none" | "scheduled" | "in_cadence" | "exhausted";
  attemptCount: number;
  totalAttempts: number;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  /** The last attempt was recorded but no email went out. */
  lastAttemptFailed: boolean;
  /** False while the automated cadence still owns this row. */
  canSendManually: boolean;
}

export function listEligibleRequests(
  limit = 50
): Promise<EligibleRequestItem[]> {
  return apiClient
    .get<{ data: EligibleRequestItem[] }>(
      `/platform/request-feedback/eligible?limit=${limit}`
    )
    .then((res) => res.data.data);
}

export type SendRequestFeedbackNowResult =
  | "sent"
  | "no_consumer_email"
  | "send_failed"
  | "already_submitted"
  | "blocked_cadence_running"
  | "claimed_elsewhere";

export function sendRequestFeedbackNow(payload: {
  requestType: RequestFeedbackRequestType;
  requestId: string;
  /** Root-only: send even though reminders are still scheduled. */
  force?: boolean;
}): Promise<SendRequestFeedbackNowResult> {
  return apiClient
    .post<{ status: SendRequestFeedbackNowResult }>(
      `/platform/request-feedback/send-now`,
      payload
    )
    .then((res) => res.data.status);
}
